// Path: frontend/src/pages/admin/components/chat-records/ChatRecordsFilter.tsx

import React from 'react';
import { Filter, Search, X, Loader2 } from 'lucide-react';

export interface FilterOption {
  id: number;
  title: string;
}

export interface FilterData {
  users: FilterOption[];
  courses: FilterOption[];
  stages: FilterOption[];
  lessons: FilterOption[];
}

export interface FilterValues {
  userId: number | '';
  courseId: number | '';
  stageId: number | '';
  lessonId: number | '';
  startDate: string;
  endDate: string;
}

interface ChatRecordsFilterProps {
  filterData: FilterData;
  values: FilterValues;
  isLoading: boolean;
  onValueChange: (key: keyof FilterValues, value: string | number | '') => void;
  onSearch: () => void;
  onReset: () => void;
}

const getTodayDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ChatRecordsFilter: React.FC<ChatRecordsFilterProps> = ({
  filterData,
  values,
  isLoading,
  onValueChange,
  onSearch,
  onReset
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Filter size={18} className="text-gray-400" />
        <span className="font-medium text-gray-700">筛选条件</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">用户</label>
          <select
            value={values.userId}
            onChange={(e) => onValueChange('userId', e.target.value ? Number(e.target.value) : '')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
            disabled={isLoading}
          >
            <option value="">全部用户</option>
            {filterData.users.map(user => (
              <option key={user.id} value={user.id}>{user.title}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">课程</label>
          <select
            value={values.courseId}
            onChange={(e) => onValueChange('courseId', e.target.value ? Number(e.target.value) : '')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
            disabled={isLoading}
          >
            <option value="">全部课程</option>
            {filterData.courses.map(course => (
              <option key={course.id} value={course.id}>{course.title}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">阶段</label>
          <select
            value={values.stageId}
            onChange={(e) => onValueChange('stageId', e.target.value ? Number(e.target.value) : '')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
            disabled={isLoading || !values.courseId}
          >
            <option value="">全部阶段</option>
            {filterData.stages.map(stage => (
              <option key={stage.id} value={stage.id}>{stage.title}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">课时</label>
          <select
            value={values.lessonId}
            onChange={(e) => onValueChange('lessonId', e.target.value ? Number(e.target.value) : '')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
            disabled={isLoading || !values.stageId}
          >
            <option value="">全部课时</option>
            {filterData.lessons.map(lesson => (
              <option key={lesson.id} value={lesson.id}>{lesson.title}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">开始日期</label>
          <input
            type="date"
            value={values.startDate}
            onChange={(e) => onValueChange('startDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">结束日期</label>
          <input
            type="date"
            value={values.endDate}
            onChange={(e) => onValueChange('endDate', e.target.value)}
            max={getTodayDate()}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
          />
        </div>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button
          onClick={onSearch}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Search size={16} />
          )}
          查询
        </button>
        <button
          onClick={onReset}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <X size={16} />
          重置
        </button>
      </div>
    </div>
  );
};

export default ChatRecordsFilter;
